import { Box } from "@mui/material";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Button,
  CircularProgress,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Benefit_Entity } from "../../entities/benefit";
import { toScaledInteger } from "../../helpers/numberHelpers";
import {
  useCreateBenefitMutation,
  useDeleteBenefitMutation,
  useUpdateBenefitMutation,
} from "../../redux/benefit/benefitEndpoints";
import { useBenefit } from "../../redux/benefit/benefitHooks";
import {
  formOpenSelector,
  selectedBenefitID,
  setFormOpen,
  setSelectedBenefitID,
} from "../../redux/filmsReducer";
import { useJobCategories } from "../../redux/jobCategory/jobCategoryHooks";
import { useProjectTypes } from "../../redux/projectType/projectTypeHooks";
import { useAskQuestion } from "../askQuestion/askQuestion";

/**
 *
 * @returns {ReactElement} FormBenefit page
 */
export function FormBenefit() {
  const benefitID = useSelector(selectedBenefitID);
  const { benefit, isLoading } = useBenefit(benefitID);
  const open = useSelector(formOpenSelector("benefit"));
  const [_benefit, setBenefit] = useState<Partial<Benefit_Entity>>({});

  const [createBenefit, { isLoading: isCreating }] = useCreateBenefitMutation();
  const [updateBenefit, { isLoading: isUpdating }] = useUpdateBenefitMutation();
  const [deleteBenefit] = useDeleteBenefitMutation();
  const askQuestion = useAskQuestion();
  const [pensionFocused, setPensionFocused] = useState(false);
  const [pensionValue, setPensionValue] = useState("");

  const [vacationFocused, setVacationFocused] = useState(false);
  const [vacationValue, setVacationValue] = useState("");

  const [welfareFocused, setWelfareFocused] = useState(false);
  const [welfareValue, setWelfareValue] = useState("");

  const [flexDivisorFocused, setFlexDivisorFocused] = useState(false);
  const [flexDivisorValue, setFlexDivisorValue] = useState("");

  const [flexMultiplierFocused, setFlexMultiplierFocused] = useState(false);
  const [flexMultiplierValue, setFlexMultiplierValue] = useState("");

  const [flexDayHoursFocused, setFlexDayHoursFocused] = useState(false);
  const [flexDayHoursValue, setFlexDayHoursValue] = useState("");

  const { projectTypes } = useProjectTypes();
  const { jobCategories } = useJobCategories();

  const selectedProjectType = projectTypes?.find(
    (pt) => pt.id === _benefit.projectTypeID
  );

  const selectedJobCategory = jobCategories?.find(
    (jc) => jc.id === _benefit.jobCategoryID
  );

  const dispatch = useDispatch();

  useEffect(() => {
    if (benefit?.id) {
      setBenefit(benefit);
      setPensionValue(((benefit.pension ?? 0) / 10000).toString());
      setVacationValue(((benefit.vacation ?? 0) / 10000).toString());
      setWelfareValue(((benefit.welfare ?? 0) / 10000).toString());
      setFlexDivisorValue(((benefit.flexDivisor ?? 0) / 10000).toString());
      setFlexMultiplierValue(
        ((benefit.flexMultiplier ?? 0) / 10000).toString()
      );
      setFlexDayHoursValue(((benefit.flexDayHours ?? 0) / 10000).toString());
    }
  }, [benefit]);

  const onClose = () => {
    dispatch(setFormOpen({ isOpen: false, formID: "benefit" }));
    dispatch(setSelectedBenefitID(undefined));
  };

  const save = () => {
    if (benefitID && _benefit) {
      updateBenefit({
        id: benefitID,
        body: {
          ..._benefit,
          pension: toScaledInteger(pensionValue, 10000),
          vacation: toScaledInteger(vacationValue, 10000),
          welfare: toScaledInteger(welfareValue, 10000),
          flexDivisor: toScaledInteger(flexDivisorValue, 10000),
          flexMultiplier: toScaledInteger(flexMultiplierValue, 10000),
          flexDayHours: toScaledInteger(flexDayHoursValue, 10000),
        },
      });
    } else if (_benefit) {
      createBenefit({
        ..._benefit,
        pension: toScaledInteger(pensionValue, 10000),
        vacation: toScaledInteger(vacationValue, 10000),
        welfare: toScaledInteger(welfareValue, 10000),
        flexDivisor: toScaledInteger(flexDivisorValue, 10000),
        flexMultiplier: toScaledInteger(flexMultiplierValue, 10000),
        flexDayHours: toScaledInteger(flexDayHoursValue, 10000),
      })
        .unwrap()
        .then((e) => {
          dispatch(setSelectedBenefitID(e.id));
        });
    }
    onClose();
  };

  const askDelete = () => {
    askQuestion("Are you sure?", ["Cancel", "Yes"], {
      subtitle: (
        <Typography>
          You are about to delete a Benefit. Please confirm this action.
        </Typography>
      ),
    }).then((i) => {
      if (i == 1) confirmDelete();
    });
  };

  const confirmDelete = async () => {
    if (benefitID) deleteBenefit(benefitID);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog layout="center" sx={{ minWidth: 520, maxWidth: "95vw" }}>
        <ModalClose onClick={onClose} />
        <Typography
          startDecorator={<i className="fa fa-hand-holding-dollar" />}
          level="h6"
          sx={{ fontWeight: "bold" }}
        >
          {benefitID ? "Edit" : "Create"} Benefit
        </Typography>
        <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                flex: 1,
                alignItems: "start",
              }}
            >
              <Autocomplete
                key={selectedProjectType?.id}
                startDecorator="Project Type:"
                options={projectTypes ?? []}
                value={selectedProjectType}
                getOptionLabel={(option) => option?.name ?? ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                slotProps={{
                  listbox: { sx: { zIndex: 9999 } },
                }}
                onChange={(_, value) =>
                  setBenefit((v) => ({
                    ...v,
                    projectTypeID: value?.id ?? undefined,
                  }))
                }
              />
              <Autocomplete
                key={selectedJobCategory?.id}
                startDecorator="Job Category:"
                options={jobCategories ?? []}
                value={selectedJobCategory}
                getOptionLabel={(option) => option?.name ?? ""}
                isOptionEqualToValue={(option, value) =>
                  option.id === value?.id
                }
                slotProps={{
                  listbox: { sx: { zIndex: 9999 } },
                }}
                onChange={(_, value) =>
                  setBenefit((v) => ({
                    ...v,
                    jobCategoryID: value?.id ?? undefined,
                  }))
                }
              />
              <Input
                fullWidth
                startDecorator="Pension:"
                value={pensionFocused ? pensionValue : `${pensionValue}%`}
                onFocus={() => setPensionFocused(true)}
                onBlur={() => {
                  setPensionFocused(false);
                }}
                onChange={(e) => setPensionValue(e.target.value)}
              />
              <Input
                fullWidth
                startDecorator="Vacation:"
                value={vacationFocused ? vacationValue : `${vacationValue}%`}
                onFocus={() => setVacationFocused(true)}
                onBlur={() => {
                  setVacationFocused(false);
                }}
                onChange={(e) => setVacationValue(e.target.value)}
              />
              <Input
                fullWidth
                startDecorator="Welfare:"
                value={welfareFocused ? welfareValue : `${welfareValue}%`}
                onFocus={() => setWelfareFocused(true)}
                onBlur={() => {
                  setWelfareFocused(false);
                }}
                onChange={(e) => setWelfareValue(e.target.value)}
              />
              <Input
                fullWidth
                startDecorator="Flex Divisor:"
                value={
                  flexDivisorFocused ? flexDivisorValue : `${flexDivisorValue}%`
                }
                onFocus={() => setFlexDivisorFocused(true)}
                onBlur={() => {
                  setFlexDivisorFocused(false);
                }}
                onChange={(e) => setFlexDivisorValue(e.target.value)}
              />
              <Input
                fullWidth
                startDecorator="Flex Multiplier:"
                value={
                  flexMultiplierFocused
                    ? flexMultiplierValue
                    : `${flexMultiplierValue}%`
                }
                onFocus={() => setFlexMultiplierFocused(true)}
                onBlur={() => {
                  setFlexMultiplierFocused(false);
                }}
                onChange={(e) => setFlexMultiplierValue(e.target.value)}
              />
              <Input
                fullWidth
                startDecorator="FlexDayHours:"
                value={
                  flexDayHoursFocused
                    ? flexDayHoursValue
                    : `${flexDayHoursValue}%`
                }
                onFocus={() => setFlexDayHoursFocused(true)}
                onBlur={() => {
                  setFlexDayHoursFocused(false);
                }}
                onChange={(e) => setFlexDayHoursValue(e.target.value)}
              />
            </Box>
          </Box>
        )}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {benefitID ? (
          <Button variant="soft" onClick={askDelete} color="danger">
            Delete
          </Button>
        ) : (
          []
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} color="neutral" variant="soft">
          Cancel
        </Button>
        <Button loading={isCreating || isUpdating} onClick={save}>
          {benefitID ? "Save" : "Create"}
        </Button>
        </Box>
      </ModalDialog>
    </Modal>
  );
}

function formatEIN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid EIN: must contain exactly 9 digits.");
    }

    // Format as XX-XXXXXXX
    return `${digits.slice(0, 2)}-${digits.slice(2)}`;
  } catch (error) {
    return "";
  }
}

function formatSSN(input: string) {
  try {
    // Remove all non-digit characters
    const digits = input.replace(/\D/g, "");

    // Ensure there are exactly 9 digits
    if (digits.length !== 9) {
      throw new Error("Invalid SSN: must contain exactly 9 digits.");
    }

    // Format as XXX-XX-XXXX
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  } catch (error) {
    return "";
  }
}
